import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import useApi from '../../../utils/useApi';

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#17497F',
    color: '#ffffff',
  },
  titleText: {
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  imageCard: {
    position: 'relative',
    height: 120,
    borderRadius: 8,
    overflow: 'hidden',
    border: '2px solid #E0E0E0',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#17497F',
      transform: 'scale(1.02)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    [theme.breakpoints.up('sm')]: {
      height: 140,
    },
  },
  imageCardSelected: {
    borderColor: '#27ae60',
    borderWidth: 3,
    boxShadow: '0 0 0 2px rgba(39, 174, 96, 0.3)',
  },
  imageThumb: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  selectOverlay: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sourceBadge: {
    position: 'absolute',
    top: 4,
    left: 4,
    backgroundColor: 'rgba(23, 73, 127, 0.85)',
    color: '#ffffff',
    borderRadius: 4,
    padding: '2px 5px',
    fontSize: '0.6rem',
    fontWeight: 600,
    backdropFilter: 'blur(4px)',
    maxWidth: '70%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  titleBadge: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.65)',
    color: '#fff',
    padding: '3px 6px',
    fontSize: '0.65rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  emptyNotice: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: '#64748b',
  },
  footerActions: {
    padding: '12px 24px',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
}));

export default function UIImageSuggestionModal({
  open,
  onClose,
  initialQuery = '',
  maxSelectable = 5,
  onSelectImages,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchTerm, setSearchTerm] = useState(initialQuery);
  const [results, setResults] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchApi] = useApi({
    method: 'get',
    url: '/storage/public-images',
  });

  const handleFetchImages = (queryToSearch) => {
    const q = queryToSearch !== undefined ? queryToSearch : searchTerm;
    if (!q || !q.trim()) return;

    setLoading(true);
    searchApi({
      params: { query: q.trim(), limit: 24 },
    })
      .then((res) => {
        setLoading(false);
        if (res && res.data && res.data.results) {
          setResults(res.data.results);
        } else {
          setResults([]);
        }
      })
      .catch((err) => {
        setLoading(false);
        setResults([]);
      });
  };

  useEffect(() => {
    if (open) {
      setSearchTerm(initialQuery);
      setSelectedUrls([]);
      if (initialQuery && initialQuery.trim()) {
        handleFetchImages(initialQuery);
      } else {
        setResults([]);
      }
    }
  }, [open, initialQuery]);

  const handleToggleSelect = (url) => {
    if (selectedUrls.includes(url)) {
      setSelectedUrls(selectedUrls.filter((item) => item !== url));
    } else {
      if (selectedUrls.length >= maxSelectable) {
        return;
      }
      setSelectedUrls([...selectedUrls, url]);
    }
  };

  const handleConfirm = () => {
    if (onSelectImages && selectedUrls.length > 0) {
      onSelectImages(selectedUrls);
    }
    if (onClose) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography className={classes.titleText}>
          🔍 Banco de Imagens
        </Typography>
        <IconButton size="small" onClick={onClose} style={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 16 }}>
        <Typography variant="body2" color="textSecondary">
          Pesquise e selecione imagens para vincular ao seu item (máx: {maxSelectable}).
        </Typography>

        <Box className={classes.searchContainer}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Digite o nome do produto ou serviço..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') handleFetchImages();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            style={{ backgroundColor: '#17497F', minWidth: 90 }}
            onClick={() => handleFetchImages()}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Buscar'}
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress style={{ color: '#17497F' }} />
          </Box>
        ) : results.length > 0 ? (
          <Grid container spacing={1} style={{ maxHeight: isMobile ? undefined : 400, overflowY: 'auto', paddingRight: 4 }}>
            {results.map((img) => {
              const targetUrl = img.thumbUrl || img.fullUrl;
              const isSelected = selectedUrls.includes(targetUrl);
              return (
                <Grid item xs={4} sm={4} md={3} key={img.id}>
                  <Box
                    className={`${classes.imageCard} ${isSelected ? classes.imageCardSelected : ''}`}
                    onClick={() => handleToggleSelect(targetUrl)}
                  >
                    <img src={img.thumbUrl} alt={img.title} className={classes.imageThumb} referrerPolicy="no-referrer" />
                    {img.source && (
                      <Box className={classes.sourceBadge}>
                        {img.source}
                      </Box>
                    )}
                    <Box className={classes.selectOverlay}>
                      {isSelected ? (
                        <CheckCircleIcon style={{ color: '#27ae60', fontSize: 22 }} />
                      ) : (
                        <Checkbox
                          checked={false}
                          size="small"
                          disabled={selectedUrls.length >= maxSelectable}
                          style={{ padding: 0 }}
                        />
                      )}
                    </Box>
                    <Typography className={classes.titleBadge} title={img.title}>
                      {img.title}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box className={classes.emptyNotice}>
            <Typography variant="body1">
              {searchTerm ? 'Nenhuma imagem encontrada para este termo.' : 'Digite um termo acima para pesquisar imagens.'}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.footerActions}>
        <Typography variant="caption" style={{ fontWeight: 600, color: selectedUrls.length >= maxSelectable ? '#e74c3c' : '#333' }}>
          Selecionadas: {selectedUrls.length} / {maxSelectable}
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          <Button onClick={onClose} color="default">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="primary"
            style={{ backgroundColor: '#27ae60' }}
            disabled={selectedUrls.length === 0}
          >
            Adicionar {selectedUrls.length > 0 ? `(${selectedUrls.length})` : ''}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
